import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Home, Trash2, Shield, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataDeletion = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!confirmed) {
      toast.error('Veuillez confirmer que vous comprenez les conséquences de cette action');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/data-deletion-request`, formData);
      setSubmitted(true);
      toast.success('Votre demande a été enregistrée');
    } catch (error) {
      console.error('Erreur soumission:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'envoi de la demande');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F5F5F5' }}>
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-md p-8">
            <CheckCircle size={64} className="mx-auto mb-4" style={{ color: '#4CAF50' }} />
            <h1 className="text-2xl font-bold mb-4" style={{ color: '#335C2E' }}>
              Demande envoyée
            </h1>
            <p className="text-gray-600 mb-6">
              Votre demande de suppression de données a été enregistrée. 
              Nous la traiterons dans un délai de 30 jours conformément à la réglementation.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Vous recevrez une confirmation par email à l'adresse : <strong>{formData.email}</strong>
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-lg text-white"
              style={{ background: '#335C2E' }}
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80"
              style={{ color: '#335C2E' }}
            >
              <ArrowLeft size={20} />
              Retour à l'accueil
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100"
              style={{ color: '#335C2E' }}
            >
              <Home size={18} />
              Accueil
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Trash2 size={48} className="mx-auto mb-4" style={{ color: '#DC3545' }} />
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#335C2E' }}>
              Suppression des données
            </h1>
            <p className="text-gray-600">
              Exercez votre droit à l'effacement de vos données personnelles (RGPD)
            </p>
          </div>

          {/* Info box */}
          <div className="mb-8 p-4 rounded-lg border" style={{ background: '#FFF3E0', borderColor: '#FFCC80' }}>
            <div className="flex gap-3">
              <AlertTriangle size={24} style={{ color: '#E65100' }} className="flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#E65100' }}>
                  Important : Cette action est irréversible
                </h3>
                <p className="text-sm" style={{ color: '#333333' }}>
                  La suppression de vos données entraînera :
                </p>
                <ul className="text-sm mt-2 space-y-1" style={{ color: '#333333' }}>
                  <li>• Suppression de votre fiche contact de notre CRM</li>
                  <li>• Suppression de vos demandes de devis et précontrats</li>
                  <li>• Suppression de vos rendez-vous</li>
                  <li>• Anonymisation de vos commentaires sur le blog</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Nom complet <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="w-full p-3 border rounded-lg"
                placeholder="Jean Dupont"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Adresse email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full p-3 border rounded-lg"
                placeholder="votre@email.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                L'adresse email associée à vos données chez Refacing VR
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Raison de la demande (optionnel)
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full p-3 border rounded-lg"
                rows="3"
                placeholder="Indiquez la raison de votre demande si vous le souhaitez..."
              />
            </div>

            {/* Confirmation checkbox */}
            <div className="p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1"
                  style={{ accentColor: '#335C2E' }}
                />
                <span className="text-sm">
                  Je comprends que cette demande entraînera la suppression définitive de mes données 
                  personnelles et que cette action est irréversible. Je confirme vouloir exercer mon 
                  droit à l'effacement conformément au RGPD.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !confirmed}
              className="w-full py-3 rounded-lg text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: '#DC3545' }}
            >
              <Trash2 size={20} />
              {submitting ? 'Envoi en cours...' : 'Soumettre ma demande de suppression'}
            </button>
          </form>

          {/* Footer info */}
          <div className="mt-8 pt-6 border-t">
            <div className="flex items-start gap-3">
              <Shield size={20} style={{ color: '#335C2E' }} className="flex-shrink-0 mt-1" />
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  <strong>Vos droits :</strong> Conformément au Règlement Général sur la Protection des Données (RGPD) 
                  et à la Loi sur la protection des renseignements personnels, vous disposez d'un droit d'accès, 
                  de rectification et de suppression de vos données.
                </p>
                <p>
                  <strong>Délai de traitement :</strong> Votre demande sera traitée dans un délai maximum de 30 jours.
                </p>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
            <button
              onClick={() => navigate('/legal/privacy_policy')}
              className="hover:underline"
              style={{ color: '#335C2E' }}
            >
              Politique de confidentialité
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => navigate('/legal/terms_of_service')}
              className="hover:underline"
              style={{ color: '#335C2E' }}
            >
              Conditions d'utilisation
            </button>
            <span className="text-gray-300">|</span>
            <a
              href="mailto:info@refacingvr.com"
              className="hover:underline"
              style={{ color: '#335C2E' }}
            >
              Nous contacter
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12" style={{ background: '#2A4A24', color: 'white' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm opacity-90">
            © 2025 Refacing VR. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DataDeletion;
